import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import {
  isUserMatch,
  queries,
  useAuthConfig,
  useAuthStore,
  useChangePassword,
  useSignIn,
} from "@theprelude/core";
import {
  Button,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Dialog,
  DialogClose,
  DialogBody,
  Form,
  notifyError,
  notifySuccess,
  PasswordFormField,
} from "@theprelude/ds";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { setPageTitle } from "@/components/app-shell/app-shell";
import EditProfileButton from "@/components/edit-profile-button";
import { SignInMethod } from "@/components/sign-in-method";
import { useConfig } from "@/hooks/use-config";
import { usePopper } from "@/hooks/use-popper";
import { passwordSchema } from "@/lib/validators";

export const Route = createFileRoute("/_app/account/profile")({
  beforeLoad() {
    setPageTitle("Profile");
  },
  component: Profile,
});

function Profile() {
  const authConfig = useAuthConfig();
  const profileQuery = useSuspenseQuery({
    ...queries.iam.getAccount(authConfig),
    select: (account) => ({
      name: account.users.find((u) => isUserMatch(account.whoami, u))?.name,
      whoami: account.whoami,
      oidc:
        account.users.find((u) => isUserMatch(account.whoami, u))?.oidc ?? "",
    }),
  });

  const { name, whoami, oidc } = profileQuery.data;

  return (
    <div className="flex h-full w-full flex-col gap-4 p-6">
      <div className={"flex min-h-[38px] items-center justify-between"}>
        <h1 className="min-h-[38px] text-2xl dark:font-light">My Profile</h1>
      </div>
      <div className="border-b border-stroke-line" />

      <div className="flex flex-col gap-y-6 py-4">
        <div className="flex flex-col">
          {name && name !== "" && (
            <>
              <div className="text-rg mb-2 leading-6 text-secondary-foreground">
                Name
              </div>
              <div className="mb-4">{name}</div>
            </>
          )}
          <div className="text-rg mb-2 leading-6 text-secondary-foreground">
            Email
          </div>

          <div className="mb-4">{whoami.handle}</div>

          <div className="text-rg mb-2 leading-6 text-secondary-foreground">
            Sign in method
          </div>

          <div className="mb-4">
            <SignInMethod oidc={oidc} />
          </div>

          <div className="mt-2 flex flex-row gap-4">
            <EditProfileButton />
            {oidc === "" && <ChangePasswordModal />}
            <ExportProfileButton />
          </div>
        </div>
      </div>
    </div>
  );
}

function ExportProfileButton() {
  const { handleExport } = useConfig();

  return (
    <Button intent="secondary" onClick={handleExport}>
      Export CLI profile
    </Button>
  );
}

const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1),
    newPassword: passwordSchema,
    confirmPassword: z.string().min(1),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type ChangePasswordForm = z.infer<typeof changePasswordSchema>;

function ChangePasswordModal() {
  const dialog = usePopper();

  return (
    <Dialog {...dialog}>
      <DialogTrigger asChild>
        <Button intent="caution">Change password</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change Password</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <UpdatePasswordForm onDone={dialog.close} />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

function UpdatePasswordForm({ onDone }: { onDone: () => void }) {
  const changePassword = useChangePassword();
  const { handle } = useAuthConfig();
  const signIn = useSignIn();
  const signOut = useAuthStore((state) => state.signOut);
  const router = useRouter();

  const form = useForm<ChangePasswordForm>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const handleSubmit = (data: ChangePasswordForm) => {
    changePassword.mutate(
      {
        oldPassword: data.currentPassword,
        newPassword: data.newPassword,
      },
      {
        onSuccess: async () => {
          onDone();
          notifySuccess({
            message: "Password changed successfully",
          });
          if (handle) {
            await signOut({ clearAccount: false });
            await signIn.mutateAsync({
              email: handle,
              password: data.newPassword,
            });
            await router.invalidate();
          }
        },
        onError: (error) => {
          notifyError({
            message: "Failed to change password",
            error,
          });
        },
      },
    );
  };

  return (
    <Form {...form} onSubmitValid={handleSubmit}>
      <div className="flex flex-col gap-4">
        <PasswordFormField
          label="Current Password"
          {...form.register("currentPassword", { required: true })}
        />
        <PasswordFormField
          label="New Password"
          {...form.register("newPassword", { required: true })}
        />
        <PasswordFormField
          label="Confirm Password"
          {...form.register("confirmPassword", { required: true })}
        />
        <div className="flex justify-end gap-2 pt-2">
          <DialogClose asChild>
            <Button intent="secondary">Cancel</Button>
          </DialogClose>
          <Button
            type="submit"
            loading={changePassword.isPending}
            disabled={changePassword.isPending}
            intent="primary"
          >
            Change Password
          </Button>
        </div>
      </div>
    </Form>
  );
}
